/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useLocale, useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import {
  useCurrentQueueItem,
  useMediaAnalytics,
  usePlayer,
} from "@packages/media";
import { useLocalStorageState } from "@packages/sdk";
import type { PropsWithChildren } from "react";
import { useContext, useEffect, useRef, useState } from "react";

import type { ThemeType } from "../../../../../../global/emotion";
import {
  colors,
  constantSemanticColors,
  spacing,
  ThemeProvider,
} from "../../../../../../global/emotion";
import { IconButton } from "../../../../../components/_base/_core/IconButton/IconButton.emotion";
import { Text } from "../../../../../components/_base/_core/Text/Text.emotion";
import { SubtitlesIcon } from "../../../../../components/_base/icons/Icon/GeneralIcons";
import { ModalDropdownMenu } from "../../../../../components/_base/modals/ModalDropdownMenu/ModalDropdownMenu.emotion";
import { ModalDropdownMenuHeader } from "../../../../../components/_base/modals/ModalDropdownMenuHeader/ModalDropdownMenuHeader.emotion";
import { ModalDropdownMenuItem } from "../../../../../components/_base/modals/ModalDropdownMenuItem/ModalDropdownMenuItem.emotion";
import type { WithButtonWrapper } from "../../../../../types/withButtonWrapper.types";
import { PlayerInternalContext } from "../../../core/MediaPlayer/MediaPlayer.emotion";
import { FragmentAsChild } from "../../../primitives/FragmentAsChild/FragmentAsChild";

const darkModeStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
});
const containerStyle: Interpolation = {
  background: colors.transparent,
};

const languageStyle: Interpolation = {
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-evenly",
  rowGap: spacing.t,
};

type SubtitlesButtonProps = WithButtonWrapper<{
  showControls: boolean;
}>;

type LanguageLabelProps = PropsWithChildren<{
  lang: string;
}>;

const LanguageLabel = ({ lang, children }: LanguageLabelProps) => {
  const locale = useLocale();
  const t = useTranslations();
  const targetLabeller = new Intl.DisplayNames([lang], { type: "language" });
  const localLabeller = new Intl.DisplayNames([locale.split("-")[0]], {
    type: "language",
  });

  return (
    <>
      <div css={languageStyle}>
        <Text as={"p"} type={"title"} size={"m"}>
          {targetLabeller.of(lang)}
        </Text>
        <Text as={"p"} type={"detail"} size={"l"}>
          {lang === locale
            ? t("language_settings_default_option")
            : localLabeller.of(lang)}
        </Text>
      </div>
      {children}
    </>
  );
};

export const SubtitlesButton = ({
  showControls,
  Wrapper = FragmentAsChild,
}: SubtitlesButtonProps) => {
  const player = usePlayer();
  const { currentItem } = useCurrentQueueItem();
  const elements = useContext(PlayerInternalContext);
  const [preferredSubtitles, setPreferredSubtitles] = useLocalStorageState<
    string | null
  >({ key: "subtitles", defaultValue: null });
  const updateInProgress = useRef<boolean>(false);
  const t = useTranslations();
  const [open, setOpen] = useState<boolean>(false);
  const { popupRef } = useContext(PlayerInternalContext);
  const analytics = useAnalytics();
  const mediaAnalytics = useMediaAnalytics();

  useEffect(() => {
    if (!showControls) {
      setOpen(false);
    }
  }, [showControls]);

  useEffect(() => {
    if (updateInProgress.current) {
      updateInProgress.current = false;
    } else if (preferredSubtitles && player?.subtitles?.length) {
      for (let s = 0; s < player.subtitles.length; s++) {
        if (player.subtitles[s].language === preferredSubtitles) {
          player.setSubtitles(s);
          break;
        }
      }
    } else {
      player?.setSubtitles(null);
    }
  }, [preferredSubtitles, player?.subtitles]);

  const turnOffSubtitles = () => {
    player.setSubtitles(null);
    elements.transcriptOpenChangeHandler(false);
    setPreferredSubtitles(null);
    analytics.track({
      event: HallowAnalyticsEvent.SelectedSubtitles,
      properties: {
        ...(mediaAnalytics.current?.mediaAnalyticsProps ?? {}),
        option: "off",
      },
    });
  };

  const handleSubtitleSelect = (idx: number) => {
    updateInProgress.current = true;
    setPreferredSubtitles(player.subtitles[idx].language);
    player.setSubtitles(idx);
    elements.transcriptOpenChangeHandler(false);
    analytics.track({
      event: HallowAnalyticsEvent.SelectedSubtitles,
      properties: {
        ...(mediaAnalytics.current?.mediaAnalyticsProps ?? {}),
        ...(mediaAnalytics.current?.refBasedProps() ?? {}),
        option: player.subtitles[idx].language,
      },
    });
  };

  const handleTosSelect = () => {
    updateInProgress.current = true;
    setPreferredSubtitles(null);
    player.setSubtitles(null);
    player.tosTrigger.current?.click();
  };

  if (player?.mediaType !== "video") return null;

  if (
    !player?.subtitles?.length &&
    !currentItem?.selected_audio?.has_transcript
  ) {
    return (
      <IconButton
        icon={
          <SubtitlesIcon.Outline
            fill={constantSemanticColors.onColorVeryHigh}
          />
        }
        variant={"transparentWhite"}
        disabled={true}
        title={t("subtitles_not_available")}
      />
    );
  }

  const contentCSS = [darkModeStyle];

  return (
    <ThemeProvider forceTheme="dark" as={null}>
      <ModalDropdownMenu
        trigger={
          <Wrapper asChild>
            <IconButton
              icon={
                elements.transcriptOpen || player.subtitleIdx !== null ? (
                  <SubtitlesIcon.Filled
                    fill={constantSemanticColors.onColorVeryHigh}
                  />
                ) : (
                  <SubtitlesIcon.Outline
                    fill={constantSemanticColors.onColorVeryHigh}
                  />
                )
              }
              variant={"transparentWhite"}
              tabIndex={0}
              title={t(
                elements.transcriptOpen || player.subtitleIdx !== null
                  ? "button_description_toggle_view_subtitles"
                  : "button_description_toggle_enable_subtitles",
              )}
            />
          </Wrapper>
        }
        open={open}
        onOpenChange={setOpen}
        dropdownMenuProps={{
          contentCSS,
          portalProps: { container: popupRef.current },
          contentProps: { forceMount: true },
        }}
        modalProps={{
          contentCSS,
          portalProps: { container: popupRef.current },
          contentProps: { forceMount: true },
        }}
      >
        <ModalDropdownMenuHeader>
          {t("subtitles_dialog_title")}
        </ModalDropdownMenuHeader>
        <ModalDropdownMenuItem
          key={"none"}
          onSelect={() => turnOffSubtitles()}
          selected={player.subtitleIdx === null && !elements.transcriptOpen}
        >
          {t("general_word_none")}
        </ModalDropdownMenuItem>
        {player.subtitles.map((track, idx) => (
          <ModalDropdownMenuItem
            key={track.id}
            onSelect={() => handleSubtitleSelect(idx)}
            selected={player.subtitleIdx === idx}
          >
            <LanguageLabel lang={track.language} />
          </ModalDropdownMenuItem>
        ))}
        {currentItem?.selected_audio?.has_transcript && (
          <ModalDropdownMenuItem
            key={"transcript"}
            onSelect={handleTosSelect}
            selected={player.subtitleIdx === null && elements.transcriptOpen}
          >
            {t("read_transcript")}
          </ModalDropdownMenuItem>
        )}
      </ModalDropdownMenu>
    </ThemeProvider>
  );
};
